<template>
  <div class="intensity-group" role="radiogroup" :aria-label="ariaLabel">
    <button
      v-for="opt in options"
      :key="opt.value"
      type="button"
      class="intensity-card"
      :class="{ selected: value === opt.value }"
      role="radio"
      :aria-checked="value === opt.value"
      :disabled="disabled"
      @click="onSelect(opt.value)"
    >
      <div class="intensity-card__head">
        <span class="intensity-card__name">{{ opt.name }}</span>
        <span v-if="opt.recommended" class="pill gold">Recommended</span>
      </div>
      <div class="intensity-card__cadence">{{ opt.cadence }}</div>
      <div class="intensity-card__desc">{{ opt.desc }}</div>
      <div v-if="summaryFor" class="intensity-card__summary">{{ summaryFor(opt.value) }}</div>
    </button>
  </div>
</template>

<script>
/**
 * IntensityCadencePicker — shared 3-card picker for the adaptive
 * question intensity feature.
 *
 * Used in two places:
 *   1. Free-tier signup wizard, Step 1 (WizardCompanyStep.vue) — sets
 *      the initial cadence on a new account. Value flows through the
 *      /freetrial API payload and is converted to intensity_state.
 *      admin_bias on the backend.
 *   2. Questions header popover — lets existing admins change
 *      their intensity at any time. Value round-trips through
 *      intensity_state.admin_bias via the cadence ↔ admin_bias
 *      helpers in src/store/intensity-mapping.ts.
 *
 * Controlled component: parent owns the `value` (`light`, `balanced`,
 * `intense`, or null for an unconfigured account) and listens for
 * `@input`. No store coupling — picker stays portable.
 *
 * About the copy:
 * Questions are delivered every day at every intensity. What changes
 * across the three cards is (a) how many questions per day and
 * (b) how often open-ended prompts appear. Lower intensity = fewer
 * questions and fewer open-ended; higher intensity = more questions
 * and more frequent open-ended prompts. This matches the AQI
 * feature's CES (Cognitive Effort Score) budget — see
 * src/store/intensity.ts.
 */

const DEFAULT_OPTIONS = [
  {
    value: 'light',
    name: 'Light',
    cadence: '2–3 daily · 1 open-ended weekly',
    desc: 'A light pulse with a weekly reflection. Best for small teams or those new to feedback.',
    recommended: false,
  },
  {
    value: 'balanced',
    name: 'Balanced',
    cadence: '3–4 daily · 2 open-ended weekly',
    desc: 'A steady rhythm of pulses and reflections. The default for most teams.',
    recommended: true,
  },
  {
    value: 'intense',
    name: 'Intense',
    cadence: 'No daily or weekly caps',
    desc: 'Every question runs on its full schedule. For teams ready to go deeper.',
    recommended: false,
  },
]

export default {
  name: 'IntensityCadencePicker',
  props: {
    value: {
      type: String,
      default: 'balanced',
      validator: (v) => v === 'light' || v === 'balanced' || v === 'intense',
    },
    /**
     * Optional override for the option list (same shape as
     * DEFAULT_OPTIONS). Use this if a surface needs different copy
     * — e.g. a paid-tier admin view that wants more clinical
     * language than the wizard's. Most callers should leave the
     * default.
     */
    options: {
      type: Array,
      default: () => DEFAULT_OPTIONS,
    },
    /**
     * Optional function `(cadence) => string` that returns a live
     * summary line shown beneath each card — typically the
     * estimated questions/day for that cadence based on the
     * intensity store's healthSummary getter. Pass `null` (default)
     * to omit the line.
     */
    summaryFor: {
      type: Function,
      default: null,
    },
    ariaLabel: {
      type: String,
      default: 'Check-in intensity',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onSelect(value) {
      if (this.disabled) return
      if (value !== this.value) this.$emit('input', value)
    },
  },
}
</script>

<style lang="scss" scoped>
.intensity-group {
  display: grid;
  // minmax(0, 1fr) forces truly equal columns even when one card's
  // min-content (e.g., title + Recommended pill) would otherwise expand it.
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
}

.intensity-card {
  appearance: none;
  border: 1px solid var(--signup-border-soft, #ece8f4);
  background: var(--signup-surface, #ffffff);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  text-align: left;
  font-family: 'Lato', system-ui, sans-serif;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  &:hover {
    border-color: var(--primary-light, #aa86e0);
    background: rgba(108, 48, 204, 0.03);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--signup-gold, #F7B924);
    outline-offset: 2px;
  }

  &.selected {
    border-color: var(--primary, #6C30CC);
    background: linear-gradient(180deg, rgba(108, 48, 204, 0.05) 0%, rgba(108, 48, 204, 0.01) 100%);
    box-shadow: 0 0 0 3px var(--primary-faint, #f2ecfb), 0 1px 2px rgba(30, 10, 60, 0.04);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.62;
    transform: none;
  }

  &:disabled:hover {
    border-color: var(--signup-border-soft, #ece8f4);
    background: var(--signup-surface, #ffffff);
  }
}

.intensity-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}

.intensity-card__name {
  font-size: 14px;
  font-weight: 800;
  color: var(--signup-text, #1a1a2e);
  letter-spacing: -0.1px;
}

.intensity-card__cadence {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--primary, #6C30CC);
  margin-top: 1px;
}

.intensity-card__desc {
  font-size: 12px;
  color: var(--signup-text-soft, #5a5a73);
  margin-top: 6px;
  line-height: 1.4;
}

.intensity-card__summary {
  font-size: 11px;
  font-weight: 600;
  color: var(--signup-text-muted, #6b7280);
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed var(--signup-border-soft, #ece8f4);
}

.pill {
  display: inline-flex;
  align-items: center;
  background: var(--primary-faint, #f2ecfb);
  color: var(--primary, #6C30CC);
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.pill.gold {
  background: rgba(247, 185, 36, 0.10);
  color: #b8860b;
  font-weight: 700;
}
</style>
